import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import style from "../account/Account.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MenuIcon from "@mui/icons-material/MenuBook";
import FunctionIcon from "@mui/icons-material/Build";
import { useState, useEffect } from "react";
import api from "../../api";
import { alerting } from "../../reducer/alerterSlice";
import { useAppDispatch, useAppSelector } from "../../reducer/hooks";
import {
	selectAppAdmin,
	setSelectedRoleEnabled,
	fetchFeatureList,
	fetchRoleDetail,
} from "../../reducer/appAdminSlice";
import {
	generateNonce,
	numberToBol,
	newGetAllParentIds,
} from "../../utils/helper";
import { getChildById } from "../../utils/treeview";
import StyledTreeItem from "../../components/StyledTreeItem";
import TreeView from "@mui/lab/TreeView";
import MinusSquare from "../../components/MinusSquare";
import PlusSquare from "../../components/PlusSquare";
import CloseSquare from "../../components/CloseSquare";
import Checkbox from "@mui/material/Checkbox";
import { IFeature, IParentFeatureList } from "../../interface/appAdminFace";
import { selectCurrentApp } from "../../reducer/appListSlice";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Box } from "@mui/material";
const initUserInputs = {
	roleName: "",
	description: "",
};
export default function PreEditRole() {
	const dispatch = useAppDispatch();
	let navigate = useNavigate();
	const state = useAppSelector(selectAppAdmin);

	const { roleList } = state;
	const appId = useAppSelector(selectCurrentApp)?.appId as number;
	const { roleId } = useParams();
	const isLost = roleList.length === 0;
	const [fetchStatus, setFetchStatus] = useState("init");
	useEffect(() => {
		if (isLost) navigate("/app-admin/role-management");
	}, [isLost]);

	useEffect(() => {
		if (isLost) return;
		const isInvalid = !(roleId && appId);
		if (isInvalid) return;
		async function fetch() {
			try {
				await Promise.all([
					dispatch(fetchFeatureList(appId)).unwrap(),
					dispatch(fetchRoleDetail(Number(roleId))).unwrap(),
				]);
				setFetchStatus("fufill");
			} catch {
				setFetchStatus("fail");
			}
		}
		fetch();
	}, [appId, roleId, isLost]);
	if (isLost) return <></>; // web was refreshed, state is lost
	if (fetchStatus !== "fufill") return <></>; //api data is not ready
	return <EditRole />;
}
function EditRole() {
	const { t } = useTranslation();
	let navigate = useNavigate();
	const { roleId } = useParams();
	const dispatch = useAppDispatch();
	const state = useAppSelector(selectAppAdmin);
	const currentApp = useAppSelector(selectCurrentApp);
	const fullFeatureList = state.featureList;
	const dbSelectedIds = state.selectedRole.FeatureListId;
	const parentFeatureList: IParentFeatureList = {
		id: -1,
		name: "All Feature",
		children: fullFeatureList,
		featureType: 1,
	};
	const [selectedIds, rawSetSelectedIds] = useState<number[]>(dbSelectedIds);
	const setSelectedIds = (tickedIds: Array<number>) => {
		const selectedIdArr = newGetAllParentIds(
			parentFeatureList,
			[...selectedIds, ...tickedIds],
			new Set(),
			new Set()
		);
		rawSetSelectedIds(selectedIdArr ? selectedIdArr : []);
	};

	const [userInputs, setUserInputs] = useState(initUserInputs);
	useEffect(() => {
		if (!dbSelectedIds.length) return;
		const theIds =
			dbSelectedIds.length > 0 ? [...dbSelectedIds, -1] : dbSelectedIds;
		setSelectedIds(theIds);
	}, [dbSelectedIds]);
	useEffect(() => {
		setUserInputs({
			roleName: state.selectedRole.roleName,
			description: state.selectedRole.description,
		});
	}, [state.selectedRole]);
	const popsError = (text: string) => dispatch(alerting("error", text));
	function handleSelectedStatus(event: any) {
		dispatch(setSelectedRoleEnabled(event.target.value));
	}
	const setInputData = (e: React.ChangeEvent<HTMLInputElement>) => {
		const key = e.target.name;
		const value = e.target.value;
		setUserInputs((data) => ({ ...data, [key]: value }));
	};
	const onRoleUpdateSave = async () => {
		// if (JSON.stringify(selectedFeatureList) === JSON.stringify(dbSelectedIds)) return popsError("No Feature Change has been made");
		if (
			!selectedIds.length ||
			(selectedIds.length === 1 && selectedIds[0] === -1)
		) {
			return popsError("Feature Cannot be Empty");
		}
		if (!userInputs.roleName || !userInputs.description) {
			return popsError("Input Field Cannot be Empty");
		}
		const nonce = generateNonce();

		const data = {
			nonce,
			roleName: userInputs.roleName,
			description: userInputs.description,
			featureIdList: selectedIds.filter((id) => id !== -1),
			isEnabled: numberToBol(state.selectedRole.status),
			updateField: 15,
		};
		let result: any = null;
		result = await api.admin.updateRole(Number(roleId), data);
		if (!result) return Error;
		navigate(-1);
		dispatch(alerting("success", "Role Save Success"));
	};

	function renderTree(nodes: IFeature | IParentFeatureList) {
		const allNode: number[] = getChildById(parentFeatureList, nodes.id);
		const onClickHandler = (nodes: IFeature | IParentFeatureList) => {
			if (
				Array.isArray(nodes.children) &&
				selectedIds.includes(nodes.id) &&
				!allNode.every((id) => selectedIds.includes(id))
			) {
				setSelectedIds(allNode);
				return;
			}

			if (
				Array.isArray(nodes.children) &&
				allNode.every((id) => selectedIds.includes(id))
			) {
				rawSetSelectedIds((prev) => prev.filter((id) => !allNode.includes(id)));
				return;
			}

			if (selectedIds.includes(nodes.id)) {
				rawSetSelectedIds((prev) => prev.filter((id) => id !== nodes.id));
			} else {
				if (nodes.id === -1) {
					rawSetSelectedIds([-1]);
					return;
				}
				setSelectedIds([nodes.id]);
			}
		};
		const treeItemLabel = (
			<Box key={nodes.id} sx={{ display: "flex", alignItems: "center" }}>
				<Checkbox
					checked={selectedIds.some((item) => item === nodes.id)}
					indeterminate={selectedIds.some(
						(item) =>
							item === nodes.id &&
							nodes.children &&
							!allNode.every((id) => selectedIds.includes(id))
					)}
					onClick={(e) => {
						e.stopPropagation();
						onClickHandler(nodes);
					}}
					disabled={state.selectedRole.appId !== currentApp?.appId}
				/>

				<div style={{ display: "flex", alignItems: "center" }}>
					<div style={{ marginRight: "4px" }}>
						{nodes?.featureType === 1 ? (
							<MenuIcon sx={{ marginTop: "3px" }} />
						) : (
							<FunctionIcon sx={{ marginTop: "6px" }} />
						)}
					</div>
					<div>{nodes.name}</div>
				</div>
			</Box>
		);

		return (
			<StyledTreeItem
				key={nodes.id}
				nodeId={nodes.id.toString()}
				label={treeItemLabel}
			>
				{Array.isArray(nodes.children)
					? nodes.children.map((node: any) => renderTree(node))
					: null}
			</StyledTreeItem>
		);
	}
	return (
		<div
			className={style["content-wrap"]}
			style={{
				height: "calc(100vh - 100px)",
				minHeight: "700px",
			}}
		>
			<div className={style["title"]}>{t("appAdmin.editRole")}</div>
			<div
				className={`${style["li-container"]} ${style["li-container-edit-user"]}`}
			>
				<div className={`${style["li-label"]} ${style["li-label-long"]}`}>
					{t("appAdmin.roleName")}
				</div>
				<div className={style["li-value"]}>
					<TextField
						key={`key-${state.selectedRole.roleName}`}
						name="roleName"
						value={userInputs.roleName}
						onChange={setInputData}
						variant="outlined"
						inputProps={{
							style: {
								padding: 5,
							},
						}}
						disabled={state.selectedRole.appId !== currentApp?.appId}
					/>
				</div>
			</div>
			<div
				className={`${style["li-container"]} ${style["li-container-edit-user"]}`}
			>
				<div className={`${style["li-label"]} ${style["li-label-long"]}`}>
					{t("appAdmin.description")}
				</div>
				<div className={style["li-value"]}>
					<TextField
						key={`key-${state.selectedRole.description}`}
						name="description"
						value={userInputs.description}
						onChange={setInputData}
						variant="outlined"
						inputProps={{
							style: {
								padding: 5,
							},
						}}
						disabled={state.selectedRole.appId !== currentApp?.appId}
					/>
				</div>
			</div>
			<div
				className={`${style["li-container"]} ${style["li-container-edit-user"]}`}
			>
				<div className={`${style["li-label"]} ${style["li-label-long"]}`}>
					{t("appAdmin.status")}
				</div>
				<div className={style["li-value"]}>
					<Select
						className={style["selectInputWithoutPadding"]}
						onChange={handleSelectedStatus}
						key={`isEnabled-${state.selectedRole.roleName}`}
						defaultValue={state.selectedRole.status}
						value={state.selectedRole.status}
						disabled={state.selectedRole.appId !== currentApp?.appId}
					>
						<MenuItem value={1}>{t("account.enabled")}</MenuItem>
						<MenuItem value={-1}>{t("account.disabled")}</MenuItem>
					</Select>
				</div>
			</div>
			<div
				className={`${style["li-container"]} ${style["li-container-edit-user"]}`}
			>
				<div className={`${style["li-label"]} ${style["li-label-long"]}`}>
					{t("appAdmin.assignedRoles")}
				</div>
			</div>
			{fullFeatureList?.length > 0 && (
				<TreeView
					className={style["featureTree"]}
					aria-label="customized"
					defaultExpanded={["-1"]}
					defaultCollapseIcon={<MinusSquare />}
					defaultExpandIcon={<PlusSquare />}
					defaultEndIcon={<CloseSquare />}
					sx={{
						height: "calc(100vh - 500px)",
						minHeight: "300px",
						flexGrow: 1,
						maxWidth: 400,
						overflowY: "auto",
					}}
				>
					{renderTree(parentFeatureList)}
				</TreeView>
			)}
			<div className={`${style["li-button"]} ${style["li-button-long"]}`}>
				{state.selectedRole.appId === currentApp?.appId && (
					<Button
						color="primary"
						variant="contained"
						onClick={onRoleUpdateSave}
					>
						{t("account.submit")}
					</Button>
				)}
				<Button
					color="primary"
					variant="contained"
					onClick={() => navigate("/app-admin/role-management")}
				>
					{t("account.back")}
				</Button>
			</div>
		</div>
	);
}
